import React, { useRef, useState } from 'react';
import { useClickAway } from 'ahooks';
import { not } from 'ramda';
import styled from 'styled-components';
import { FormattedMessage, useIntl } from 'react-intl';
import { DownOutlined, LogoutOutlined, UserOutlined } from '@ant-design/icons';
import { Avatar, Button, Divider, Spin } from 'antd';
import { useNavigate } from 'react-router';
import ImprintIconSVG from 'assets/images/icons/paragraph.svg';
import languages from 'utils/languages';
import { getReferralLink } from 'utils/advertisingLinks';
import StyledDropdown from 'Common/Components/Dropdown';
import pkgJson from '../../../../package.json';

const { version: appVersion } = pkgJson;

export type Props = {
  accountName: string;
  handleLogout: () => void;
  switchLanguage: (lang: string) => void;
  currentLanguage: string;
  storeName: string;
  accountMail: string;
  isLoading: boolean;
};

const StyledReferralLink = styled.a`
  color: rgba(0, 0, 0, 0.65);
  &:hover {
    color: #0099ff;
  }
`;

const CustomIcon = ({ component: Component, ...rest }: { component: any }) => (
  <Component style={{ fontSize: 16 }} {...rest} />
);

const StyledAvatar = styled(Avatar)`
  background-color: ${({ theme }) => theme.primaryColor};
  flex: none;
`;

const UserInfo = styled.span`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  line-height: 1.3;

  .name {
    color: #595959;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .email {
    color: rgba(0, 0, 0, 0.45);
    font-size: 12px;
    white-space: nowrap;
  }
`;

const UserDropdown: React.FC<Props> = ({
  storeName,
  accountMail,
  accountName,
  handleLogout,
  switchLanguage,
  currentLanguage,
  isLoading,
}) => {
  const navigate = useNavigate();
  const [showMenu, setShowMenu] = useState(false);
  const [showLangMenu, setShowLangMenu] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  const intl = useIntl();

  useClickAway(() => {
    setShowMenu(false);
  }, ref);

  const displayName = storeName || accountName;

  return (
    <div>
      <span ref={ref}>
        <StyledDropdown
          width="250px"
          css="padding: 20px !important;margin-top: 8px!important;"
        >
          <Button
            css="display: flex; align-items: center; gap: 10px; height: auto; padding: 6px 14px;"
            onClick={() => setShowMenu(not)}
          >
            <StyledAvatar icon={<UserOutlined />} size={35} />
            <UserInfo>
              {isLoading ? (
                <Spin size="small" />
              ) : (
                <>
                  {displayName && <span className="name">{displayName}</span>}
                  {accountMail && <span className="email">{accountMail}</span>}
                </>
              )}
            </UserInfo>
            <DownOutlined
              style={{ width: 18, fontSize: 12, fontWeight: '800' }}
            />
          </Button>
          <div className={`menu ${showMenu ? 'visible' : 'hidden'}`}>
            <Button
              type="text"
              danger
              aria-label="Log out"
              onClick={() => handleLogout()}
            >
              <FormattedMessage defaultMessage="Logout" />
              <LogoutOutlined />
            </Button>
            <Divider css="margin: 4px" />
            <Button
              className={`dropdown-btn ${showLangMenu ? 'active' : 'inactive'}`}
              aria-label="Language selection"
              onClick={() => setShowLangMenu(not)}
            >
              <FormattedMessage defaultMessage="Languages" />
              <DownOutlined />
            </Button>

            <div
              className={`submenu ${showLangMenu ? 'visible' : 'hidden'}`}
              css="margin-left: 5px !important;"
            >
              {languages.map(
                ({ active, prefix, flagIcon, name }) =>
                  active && (
                    <Button
                      type="default"
                      key={prefix}
                      disabled={currentLanguage === prefix}
                      className={currentLanguage === prefix ? 'active' : ''}
                      onClick={() => switchLanguage(prefix)}
                      css="padding-top: 5px !important;margin-bottom:3px;padding-right: 10px !important"
                    >
                      <FormattedMessage {...name} />
                      <span>{flagIcon}</span>
                    </Button>
                  )
              )}
            </div>
            <Button
              type="default"
              aria-label="Imprint"
              onClick={() => navigate('/imprint')}
            >
              <FormattedMessage defaultMessage="Imprint" />
              <CustomIcon component={ImprintIconSVG} />
            </Button>
            <Button>
              <StyledReferralLink
                href={getReferralLink(intl.locale.toLowerCase())}
                target="_blank"
                rel="noopener noreferrer"
              >
                <FormattedMessage defaultMessage="Earn up to 500€" />
              </StyledReferralLink>
            </Button>
            <Divider css="margin: 8px" />
            <small>
              <FormattedMessage
                defaultMessage="App version: {version}"
                values={{ version: appVersion }}
              />
            </small>
          </div>
        </StyledDropdown>
      </span>
    </div>
  );
};

export default UserDropdown;
